import { useEffect } from 'react';
import seoPages from '../../seo/seoData.json';

const BASE_URL = 'https://www.getbentobox.in';

// Page titles, descriptions and keywords live in src/seo/seoData.json so the
// Express server (server.js) can put the same tags into the HTML it sends.
const seoData = Object.fromEntries(
    Object.entries(seoPages).map(([key, page]) => [
        key,
        { ...page, canonical: page.path === '/' ? BASE_URL : `${BASE_URL}${page.path}` },
    ])
);

// Helper to update or create a meta tag
const updateMetaTag = (attribute, value, content) => {
    let element = document.querySelector(`meta[${attribute}="${value}"]`);
    if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, value);
        document.head.appendChild(element);
    }
    element.setAttribute('content', content);
    element.setAttribute('data-seo', 'true');
    return element;
};

// Helper to update or create a link tag identified by rel + data-seo
const updateLinkTag = (rel, href) => {
    // Always look for the data-seo-marked canonical first so we don't create duplicates
    let element = document.querySelector(`link[rel="${rel}"][data-seo="true"]`);
    if (!element) {
        // Also check for any existing canonical that might have been set elsewhere
        element = document.querySelector(`link[rel="${rel}"]`);
    }
    if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
    }
    element.setAttribute('href', href);
    element.setAttribute('data-seo', 'true');
    return element;
};

// Helper to update or create a script tag for structured data
const updateScriptTag = (id, content) => {
    let element = document.getElementById(id);
    if (!element) {
        element = document.createElement('script');
        element.setAttribute('type', 'application/ld+json');
        element.setAttribute('id', id);
        document.head.appendChild(element);
    }
    element.setAttribute('data-seo', 'true');
    element.textContent = content;
    return element;
};

/**
 * SEO Component
 *
 * Usage:
 *   <SEO pageName="blog" />
 *   <SEO pageName="blogpost" customTitle="My Post | BentoBox" customDescription="..." customCanonical="https://..." customOgImage="..." />
 *   <SEO pageName="author"  customTitle="Author Name | BentoBox" customCanonical="https://..." />
 */
const SEO = ({
    pageName = 'home',
    indexPage = true,
    customTitle,
    customDescription,
    customKeywords,
    customCanonical,
    customOgImage,
    customStructuredData,
}) => {
    const baseMeta = seoData[pageName] || seoData.home;
    const structuredDataJson = customStructuredData ? JSON.stringify(customStructuredData) : '';
    const meta = {
        title: customTitle || baseMeta.title,
        description: customDescription || baseMeta.description,
        keywords: customKeywords || baseMeta.keywords,
        canonical: customCanonical || baseMeta.canonical,
        ogImage: customOgImage || `${BASE_URL}/og/default.jpg`,
    };

    useEffect(() => {
        // ── Title ───────────────────────────────────────────────────────────
        document.title = meta.title;

        // ── Basic meta ──────────────────────────────────────────────────────
        updateMetaTag('name', 'description', meta.description);
        updateMetaTag('name', 'keywords', meta.keywords);

        // ── Canonical ───────────────────────────────────────────────────────
        // This is critical: one canonical per page, always matching the actual URL
        updateLinkTag('canonical', meta.canonical);

        // ── Robots ──────────────────────────────────────────────────────────
        updateMetaTag('name', 'robots', indexPage ? 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1' : 'noindex, nofollow');

        // ── Open Graph ──────────────────────────────────────────────────────
        updateMetaTag('property', 'og:type', pageName === 'blogpost' ? 'article' : 'website');
        updateMetaTag('property', 'og:title', meta.title);
        updateMetaTag('property', 'og:description', meta.description);
        updateMetaTag('property', 'og:image', meta.ogImage);
        updateMetaTag('property', 'og:image:width', '1200');
        updateMetaTag('property', 'og:image:height', '630');
        updateMetaTag('property', 'og:url', meta.canonical);
        updateMetaTag('property', 'og:site_name', 'BentoBox');
        updateMetaTag('property', 'og:locale', 'en_IN');

        // ── Twitter Card ────────────────────────────────────────────────────
        updateMetaTag('name', 'twitter:card', 'summary_large_image');
        updateMetaTag('name', 'twitter:title', meta.title);
        updateMetaTag('name', 'twitter:description', meta.description);
        updateMetaTag('name', 'twitter:image', meta.ogImage);
        updateMetaTag('name', 'twitter:site', '@getbentobox_');
        updateMetaTag('name', 'twitter:creator', '@getbentobox_');

        // ── Authorship ──────────────────────────────────────────────────────
        updateMetaTag('name', 'author', 'BentoBox');
        updateMetaTag('name', 'publisher', 'BentoBox');

        // ── Schema.org: Organization ────────────────────────────────────────
        // Organization / FoodEstablishment / WebSite schema lives once in
        // public/index.html (single @graph) so there is no duplicate entity.

        // ── Schema.org: Page-specific (BreadcrumbList / Article / etc.) ─────
        if (structuredDataJson) {
            updateScriptTag('seo-page-schema', structuredDataJson);
        }

        // ── Cleanup ─────────────────────────────────────────────────────────
        return () => {
            document.querySelectorAll('[data-seo="true"]').forEach((el) => el.remove());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meta.title, meta.description, meta.keywords, meta.canonical, meta.ogImage, indexPage, structuredDataJson]);

    return null;
};

export default SEO;
